import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { Image } from 'antd';
import { FileOutlined, LinkOutlined } from '@ant-design/icons';
import AppModal from '@/pages/inside/tenant/AppModal';
import { Container } from '@/components/ui/Container';
import {
    AttachmentCardsRow,
    CreateRoleModalBody,
    ModalChromeHeading,
    ModalBlockLabel,
    PanelSectionLabel,
    MutedText,
    CardDescriptionInner,
    BoardUnderlineTabRow,
    BoardUnderlineTabInner,
    BoardUnderlineTab,
    BoardUnderlineTabLine,
    BoardUnderlineTabPrimaryLabel,
    BoardUnderlineTabCountBadge,
    EmptyStateBlock,
    EmptyStateIcon,
    EmptyStateTitle,
    EmptyStateDescription,
} from '@/pages/inside/tenant/style';
import { GlobalImagePreviewStyles } from '@/components/ui/GlobalImagePreviewStyles';
import { Markdown } from '@/components/ui/Markdown';
import StatusBadge from '@/components/ui/StatusBadge';
import { SprintTaskCard } from '@/components/sprint/SprintTaskCard';
import TaskAttachmentTile from '@/components/module/projects/task/TaskAttachmentTile';
import {
    getSprintTaskApiId,
    getAvatarColor,
    getInitials,
    buildSubmissionAttachmentTabSlices,
    isLikelyImageAttachmentUrl,
    normalizePhaseLinkUrl,
    resolveAttachmentUrl,
} from '@hooks/projects/projectHelpers';
import { UserAvatarWithRoleTooltip } from '@/components/ui/UserAvatarWithRoleTooltip';
import { formatLongDateTimeWithPipe } from '@utils/dateFormating.util';
import { resolveDescriptionMentionsForDisplay } from '@utils/richTextMentions';

function getSubmittedAtLabel(rawSubmission) {
    const raw =
        rawSubmission?.submittedAt ??
        rawSubmission?.submitted_at ??
        rawSubmission?.createdAt ??
        rawSubmission?.created_at ??
        null;
    if (!raw) return null;
    const formatted = formatLongDateTimeWithPipe(raw);
    return formatted === '—' ? null : formatted;
}

function getSubmittedByPresentation(rawSubmission) {
    // Expected API shape: submittedBy: { name, id }
    const user =
        rawSubmission?.submittedBy ??
        rawSubmission?.submitted_by ??
        rawSubmission?.createdBy ??
        rawSubmission?.created_by ??
        null;
    const name =
        rawSubmission?.submittedByName ??
        rawSubmission?.submitted_by_name ??
        user?.name ??
        user?.fullName ??
        user?.email ??
        rawSubmission?.submittedByEmail ??
        null;
    if (!name) return null;
    const id = user && typeof user === 'object' ? user?.id ?? user?._id ?? null : null;
    const roleLabel = user?.role?.name ?? user?.roleName ?? user?.role ?? null;
    const initials = user?.initials ?? getInitials(name);
    const color = user?.color ?? getAvatarColor(id || name);
    return { name, roleLabel, initials, color };
}

export default function QASubmissionDetailModal({
    open,
    onClose,
    sidLabel,
    submissionTitle,
    submissionStatus,
    descriptionMarkdown,
    tasks = [],
    rawSubmission,
    defaultTab = 'tasks',
    onTaskClick,
}) {
    const [detailTab, setDetailTab] = useState(defaultTab || 'tasks');
    const [previewImageSrc, setPreviewImageSrc] = useState('');

    useEffect(() => {
        if (!open) return;
        if (!defaultTab) return;
        setDetailTab(defaultTab);
    }, [open, defaultTab]);

    const { attachmentCount, attachmentLinks, attachmentFilesPresentation } = useMemo(
        () => buildSubmissionAttachmentTabSlices(rawSubmission),
        [rawSubmission],
    );
    const taskCount = Array.isArray(tasks) ? tasks.length : 0;
    const submittedAtLabel = getSubmittedAtLabel(rawSubmission);
    const submittedBy = getSubmittedByPresentation(rawSubmission);
    const resolvedDescription = useMemo(() => {
        const dm =
            rawSubmission?.qaSubmission?.descriptionMentions ??
            rawSubmission?.qaSubmission?.notesMentions ??
            rawSubmission?.descriptionMentions ??
            rawSubmission?.notesMentions ??
            null;
        return resolveDescriptionMentionsForDisplay(descriptionMarkdown || '', dm) || '';
    }, [descriptionMarkdown, rawSubmission]);

    // Match TaskDetailModal chrome: id line, title, then meta row (date left, avatar right).
    const title = (
        <Container $display="flex" $flexDirection="column" $gap="4px">
            <MutedText style={{ fontSize: 12, margin: 0 }}>{sidLabel}</MutedText>
            <ModalChromeHeading style={{ fontSize: 20 }}>{submissionTitle}</ModalChromeHeading>
            {submittedAtLabel || submittedBy?.name ? (
                <Container $display="flex" $alignItems="center" $justifyContent="space-between" $gap="10px" $flexWrap="wrap">
                    {submittedAtLabel ? (
                        <MutedText style={{ margin: 0, fontSize: 11, whiteSpace: 'nowrap' }}>
                            Submitted {submittedAtLabel}
                        </MutedText>
                    ) : (
                        <span />
                    )}
                    {submittedBy?.name ? (
                        <div
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                            }}
                        >
                            <UserAvatarWithRoleTooltip
                                name={submittedBy.name}
                                roleLabel="Submitted by"
                                initials={submittedBy.initials}
                                color={submittedBy.color}
                                size={40}
                                fontSize={13}
                            />
                        </div>
                    ) : null}
                </Container>
            ) : null}
        </Container>
    );

    const handleMarkdownLinkClick = useCallback(({ event, href, isImage }) => {
        const resolved = resolveAttachmentUrl(href);
        if (!resolved) return;
        if (isImage || isLikelyImageAttachmentUrl(resolved)) {
            event?.preventDefault?.();
            event?.stopPropagation?.();
            setPreviewImageSrc(resolved);
        }
    }, []);

    return (
        <AppModal
            size="lg"
            lgWidth="min(1180px, calc(100vw - 48px))"
            open={open}
            onCancel={onClose}
            title={title}
            footer={null}
        >
            <GlobalImagePreviewStyles />
            <CreateRoleModalBody
                $flush
                style={{
                    height: '70vh',
                    maxHeight: '70vh',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <Container $display="flex" $flexDirection="column" $gap="16px" style={{ flex: 1, minHeight: 0 }}>
                    {/* Same pattern as TaskDetailModal: label row + right pill */}
                    <div>
                        <Container $display="flex" $alignItems="center" $justifyContent="space-between" $gap="12px">
                            <ModalBlockLabel as="div" style={{ display: 'block', marginBottom: 8 }}>
                                Submission description
                            </ModalBlockLabel>
                            {submissionStatus ? (
                                <div style={{ marginBottom: 8, flexShrink: 0 }}>
                                    <StatusBadge status={submissionStatus} size="small" />
                                </div>
                            ) : (
                                <span style={{ marginBottom: 8 }} />
                            )}
                        </Container>
                        {descriptionMarkdown ? (
                            <div style={{ maxHeight: 220, overflowY: 'auto', paddingRight: 4 }}>
                                <Markdown
                                    source={resolvedDescription}
                                    component={CardDescriptionInner}
                                    richInlineAnchors
                                    linkPrimary
                                    emptyFallback="—"
                                    onLinkClick={handleMarkdownLinkClick}
                                />
                            </div>
                        ) : (
                            <MutedText style={{ margin: 0 }}>—</MutedText>
                        )}
                    </div>

                    <BoardUnderlineTabRow style={{ marginTop: 4, marginBottom: 0 }}>
                        <BoardUnderlineTabInner style={{ paddingLeft: 0, paddingRight: 0 }}>
                            <BoardUnderlineTab type="button" $active={detailTab === 'tasks'} onClick={() => setDetailTab('tasks')}>
                                <BoardUnderlineTabLine>
                                    <BoardUnderlineTabPrimaryLabel $active={detailTab === 'tasks'} style={{ fontSize: 12 }}>
                                        Tasks
                                    </BoardUnderlineTabPrimaryLabel>
                                    <BoardUnderlineTabCountBadge $active={detailTab === 'tasks'}>
                                        {taskCount}
                                    </BoardUnderlineTabCountBadge>
                                </BoardUnderlineTabLine>
                            </BoardUnderlineTab>
                            <BoardUnderlineTab type="button" $active={detailTab === 'attachments'} onClick={() => setDetailTab('attachments')}>
                                <BoardUnderlineTabLine>
                                    <BoardUnderlineTabPrimaryLabel $active={detailTab === 'attachments'} style={{ fontSize: 12 }}>
                                        Attachments
                                    </BoardUnderlineTabPrimaryLabel>
                                    <BoardUnderlineTabCountBadge $active={detailTab === 'attachments'}>
                                        {attachmentCount}
                                    </BoardUnderlineTabCountBadge>
                                </BoardUnderlineTabLine>
                            </BoardUnderlineTab>
                        </BoardUnderlineTabInner>
                    </BoardUnderlineTabRow>

                    <div style={{ paddingRight: 4, flex: 1, minHeight: 0, overflowY: 'auto' }}>
                        {detailTab === 'tasks' && (
                            <Container $display="flex" $flexDirection="column" $gap="10px" style={{ minHeight: '100%' }}>
                                {taskCount === 0 ? (
                                    <EmptyStateBlock style={{ minHeight: '100%', padding: '36px 24px' }}>
                                        <EmptyStateIcon>
                                            <FileOutlined />
                                        </EmptyStateIcon>
                                        <EmptyStateTitle>No tasks</EmptyStateTitle>
                                        <EmptyStateDescription>Tasks in this submission will appear here.</EmptyStateDescription>
                                    </EmptyStateBlock>
                                ) : (
                                    <Container $display="flex" $flexDirection="column" $gap="10px">
                                        {tasks.map((t, idx) => {
                                            const key = getSprintTaskApiId(t) ?? t?.id ?? t?.taskId ?? `task-${idx}`;
                                            return (
                                                <SprintTaskCard
                                                    key={String(key)}
                                                    task={t}
                                                    variant="readOnly"
                                                    kanbanColumn="todo"
                                                    kanbanColumnTintOnly
                                                    onSelect={() => onTaskClick?.(t)}
                                                />
                                            );
                                        })}
                                    </Container>
                                )}
                            </Container>
                        )}
                        {detailTab === 'attachments' && (
                            <Container $display="flex" $flexDirection="column" $gap="10px" style={{ minHeight: '100%' }}>
                                {attachmentCount === 0 ? (
                                    <EmptyStateBlock style={{ minHeight: '100%', padding: '36px 24px' }}>
                                        <EmptyStateIcon>
                                            <FileOutlined />
                                        </EmptyStateIcon>
                                        <EmptyStateTitle>No attachments</EmptyStateTitle>
                                        <EmptyStateDescription>Add files or links to see them here.</EmptyStateDescription>
                                    </EmptyStateBlock>
                                ) : (
                                    <>
                                        {attachmentLinks.length > 0 ? (
                                            <Container $display="flex" $flexDirection="column" $gap="8px">
                                                <PanelSectionLabel style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 0, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em', opacity: 0.75 }}>
                                                    <LinkOutlined />
                                                    Links
                                                </PanelSectionLabel>
                                                <Container $display="flex" $flexWrap="wrap" $gap="8px">
                                                    {attachmentLinks.map((item, idx) => {
                                                        const href = normalizePhaseLinkUrl(item.rawUrl);
                                                        const label = (item.name || href || 'Link').trim();
                                                        return (
                                                            <a key={`${item.id}-${idx}`} href={href || '#'} target="_blank" rel="noopener noreferrer">
                                                                {label}
                                                            </a>
                                                        );
                                                    })}
                                                </Container>
                                            </Container>
                                        ) : null}

                                        {attachmentFilesPresentation.length > 0 ? (
                                            <Container $display="flex" $flexDirection="column" $gap="8px">
                                                <PanelSectionLabel style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 0, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em', opacity: 0.75 }}>
                                                    <FileOutlined />
                                                    Attachments
                                                </PanelSectionLabel>
                                                <AttachmentCardsRow>
                                                    {attachmentFilesPresentation.map(({ item, href, isImage, ext }, idx) => (
                                                        <TaskAttachmentTile
                                                            key={`${item.id ?? 'att'}-${idx}`}
                                                            href={href}
                                                            title={item.name}
                                                            isImage={isImage}
                                                            ext={ext}
                                                            onPreviewImage={(url) => setPreviewImageSrc(url)}
                                                            onOpenFile={(url) => window.open(url, '_blank', 'noopener,noreferrer')}
                                                        />
                                                    ))}
                                                </AttachmentCardsRow>
                                            </Container>
                                        ) : null}
                                    </>
                                )}
                            </Container>
                        )}
                    </div>
                </Container>
            </CreateRoleModalBody>

            <Image
                style={{ display: 'none' }}
                src={previewImageSrc || undefined}
                preview={{
                    visible: !!previewImageSrc,
                    src: previewImageSrc || undefined,
                    rootClassName: 'global-image-preview-root',
                    onVisibleChange: (visible) => {
                        if (!visible) setPreviewImageSrc('');
                    },
                }}
            />
        </AppModal>
    );
}

